import { useEffect, useRef } from 'react';
import { useAuthStore as useAuthSdk } from '@xhype/card-app-sdk';
import { useSmartWallet } from '@package/smart-wallet/providers';
import { useAuthStore } from '@package/smart-wallet/store';

// Buffer time in seconds before actual expiration (2 minutes)
const EXPIRATION_BUFFER_SECONDS = 120;

/**
 * Decodes a JWT token and extracts the payload
 */
const decodeJWT = (token: string): { exp?: number } | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[AutoLogout] Invalid JWT format: expected 3 parts');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('[AutoLogout] Error decoding JWT:', error);
    return null;
  }
};

/**
 * Checks if a JWT token is expired or about to expire
 * Returns true if token is expired or will expire within the buffer time
 */
const isJWTExpired = (token: string): boolean => {
  const decoded = decodeJWT(token);
  
  // Validate that exp claim exists
  if (!decoded) {
    console.error('[AutoLogout] Failed to decode JWT');
    return false;
  }

  if (!decoded.exp) {
    console.error('[AutoLogout] JWT missing exp claim');
    return false;
  }

  // Validate that exp is a valid number
  if (typeof decoded.exp !== 'number' || decoded.exp <= 0) {
    console.error('[AutoLogout] Invalid exp claim value:', decoded.exp);
    return false;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  
  // Apply buffer: logout BEFORE actual expiration
  const bufferTime = EXPIRATION_BUFFER_SECONDS * 1000;
  const expirationWithBuffer = expirationTime - bufferTime;

  const isExpired = currentTime >= expirationWithBuffer;

  if (isExpired) {
    const actualExpiration = new Date(expirationTime).toLocaleString();
    const timeUntilExpiration = Math.floor((expirationTime - currentTime) / 1000);
    console.warn(
      `[AutoLogout] JWT expired or about to expire. Actual expiration: ${actualExpiration}, Time remaining: ${timeUntilExpiration}s`
    );
  }

  return isExpired;
};

/**
 * Hook that monitors JWT expiration and automatically logs out the user
 * when the JWT token expires or is about to expire (with buffer)
 */
export const useAutoLogout = () => {
  const { logout } = useSmartWallet();
  const { jwt, clearJwt } = useAuthSdk();
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isLoggingOutRef = useRef(false);

  useEffect(() => {
    // Only setup check if user is logged in and has a JWT
    if (!isLoggedIn || !jwt) {
      return;
    }

    const checkJWTExpiration = async () => {
      if (isLoggingOutRef.current) {
        return;
      }

      if (isJWTExpired(jwt)) {
        console.warn('[AutoLogout] JWT has expired');
        isLoggingOutRef.current = true;

        // Clear the interval to prevent multiple triggers
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }

        console.log('[AutoLogout] Triggering automatic logout...');

        try {
          clearJwt();
          await logout();
        } catch (error) {
          console.error('[AutoLogout] Error during logout:', error);
        } finally {
          isLoggingOutRef.current = false;
        }
      }
    };

    // Check immediately on mount
    checkJWTExpiration();

    // Check every 30 seconds
    checkIntervalRef.current = setInterval(checkJWTExpiration, 30000);

    // Cleanup function
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [isLoggedIn, jwt, logout, clearJwt]);

  return null;
};
